import { useQuery } from '@tanstack/react-query'
import { extService } from '@/services/extService'
import { notificationService } from '@/services/notificationService'
import { payoutRadarService } from '@/services/payoutRadarService'
import { webhookRequestsService } from '@/services/partnerHooksService'
import { useCan } from '@/stores/auth'
import type { BadgeKey } from './types'

export const badgeKeys = { notifications: ['badges', 'notifications'] as const }

/**
 * Live counters shown in the sidebar. Each counter polls at a modest interval and only when the user
 * can see the related module; react-query dedupes the request across every component that reads it.
 */
export function useNavBadges(): Partial<Record<BadgeKey, number>> {
  const canNotifications = useCan('notifications:read')
  const canFinance = useCan('finance:read')
  const canIntegrations = useCan('integrations:read')
  const notifications = useQuery({
    queryKey: badgeKeys.notifications,
    queryFn: ({ signal }) => notificationService.count(signal),
    enabled: canNotifications,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
    meta: { silent: true },
  })
  const alerts = useQuery({ queryKey: ['badges', 'alerts'], queryFn: ({ signal }) => extService.notifications({ unread: true, limit: 1 }, signal), enabled: canNotifications, refetchInterval: 45_000, staleTime: 20_000, retry: false, meta: { silent: true } })
  const approvals = useQuery({ queryKey: ['badges', 'approvals'], queryFn: ({ signal }) => extService.approvals(signal), enabled: canNotifications, refetchInterval: 60_000, staleTime: 30_000, retry: false, meta: { silent: true } })
  // payouts radar: violations nobody has picked up yet (open, not acknowledged)
  const radar = useQuery({ queryKey: ['badges', 'payoutRadar'], queryFn: ({ signal }) => payoutRadarService.count(signal), enabled: canFinance, refetchInterval: 60_000, staleTime: 30_000, retry: false, meta: { silent: true } })
  // partner companies waiting for a webhook approval
  const webhookRequests = useQuery({ queryKey: ['badges', 'webhookRequests'], queryFn: ({ signal }) => webhookRequestsService.count(signal), enabled: canIntegrations, refetchInterval: 60_000, staleTime: 30_000, retry: false, meta: { silent: true } })
  return { webhookRequests: webhookRequests.data || undefined, notifications: (notifications.data ?? 0) + (alerts.data?.unread ?? 0), alerts: alerts.data?.unread, approvals: approvals.data?.length, payoutRadar: radar.data || undefined }
}
